import { makeStyles } from '@material-ui/core';

const important = <T extends string>(val: T) => `${val} !important` as unknown as T;

const WNSTN_TRIGGER_GRADIENT_COLORS = ['#1570EF', '#AD0AE7', '#FF7D73', '#60ABFB'] as const;

const PANEL_RIGHT_OFFSET = 20;
const HEADER_HEIGHT = 74;

const HEADER_CONTENT_HEIGHT = 'clamp(4.8rem, 7.3rem, 8vh)';
const HEADER_TOP_MOBILE = `calc(${HEADER_CONTENT_HEIGHT} + 0.1rem)`;

const [g0, g1, g2, g3] = WNSTN_TRIGGER_GRADIENT_COLORS;
const WNSTN_TRIGGER_CONIC_SNAKE = `conic-gradient(from 0deg, ${g0}, ${g1}, ${g2}, ${g3}, ${g0})`;

export const useStyles = makeStyles((theme) => ({
    '@keyframes iconPop': {
        '0%': { opacity: 0, transform: 'scale(0.6) rotate(-15deg)' },
        '100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
    },
    '@keyframes gradientBorderRotate': {
        '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
        '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
    },
    wrapper: {
        position: 'fixed',
        top: HEADER_HEIGHT,
        right: PANEL_RIGHT_OFFSET,
        width: 760,
        height: 660,
        maxWidth: `calc(100vw - ${PANEL_RIGHT_OFFSET * 2}px)`,
        maxHeight: `calc(100vh - ${HEADER_HEIGHT}px - ${PANEL_RIGHT_OFFSET * 2}px)`,
        zIndex: 1300,
        boxShadow: '0px 8px 24px 0 rgba(0, 0, 0, 0.2)',
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        transformOrigin: 'top right',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        [theme.breakpoints.down('xs')]: {
            top: HEADER_HEIGHT,
            right: 0,
            bottom: 0,
            left: 0,
            width: 'auto',
            maxWidth: 'none',
            height: 'auto',
            maxHeight: `calc(100dvh - ${HEADER_TOP_MOBILE})`,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            borderRadius: 0,
            boxShadow: 'none',
            zIndex: theme.zIndex.drawer - 1,
        },
    },
    wrapperOpen: {
        opacity: 1,
        transform: 'scale(1)',
        pointerEvents: 'auto',
        visibility: 'visible',
    },
    wrapperClosed: {
        opacity: 0,
        transform: 'scale(0.95)',
        pointerEvents: 'none',
        visibility: 'hidden',
    },
    // WNSTN script sets position:fixed + height:667px as inline styles on this element.
    // !important beats inline styles; position:absolute confines it inside wrapper's overflow:hidden.
    container: {
        position: important('absolute'),
        top: important('0'),
        bottom: important('0'),
        height: important('auto'),
    },
    gradientBorder: {
        position: 'relative',
        overflow: 'hidden',
        padding: 1.5,
        borderRadius: 8,
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'center',
        verticalAlign: 'middle',
        width: 'auto',
        height: 40,
        flexShrink: 0,
        boxSizing: 'border-box',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 'min(380%, 120vw)',
            aspectRatio: '1 / 1',
            height: 'auto',
            zIndex: 0,
            background: WNSTN_TRIGGER_CONIC_SNAKE,
            transform: 'translate(-50%, -50%)',
            transformOrigin: 'center center',
            animation: '$gradientBorderRotate 4s linear infinite',
        },
        [theme.breakpoints.down('xs')]: {
            height: 40,
        },
        '@media (prefers-reduced-motion: reduce)': {
            '&::before': {
                animation: 'none',
            },
        },
    },
    trigger: {
        position: 'relative',
        zIndex: 1,
        background: theme.palette.background.default,
        borderRadius: 7,
        padding: '5px 9px',
        minWidth: 'unset',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
        lineHeight: 1,
        '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 7,
            background: 'transparent',
            transition: 'background 0.15s ease',
            pointerEvents: 'none',
        },
        '&:hover': {
            background: theme.palette.background.default,
        },
        '&:hover::before': {
            background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)',
        },
        '& .MuiButton-label': {
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
        },
    },
    triggerIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
        animation: '$iconPop 0.35s ease both',
    },
}));
